import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Power, Search, Users2, IdCard, Award, Briefcase, Layers } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, apiErrorMessage } from "@/lib/api";

/**
 * Halaman Master Data Kepegawaian — 5 koleksi master dalam tab terpisah:
 *   1. Status Kepegawaian
 *   2. Bidang Kerja
 *   3. Jabatan (struktural)
 *   4. Kode Pangkat
 *   5. Tingkat Jabatan
 *
 * Endpoint:   /api/master/kepegawaian/{kind}
 * Dipakai sebagai FK pada data pegawai.
 */

const TABS = [
  { kind: "status-kepegawaian", label: "Status Kepegawaian", icon: IdCard, placeholder: "Contoh: Pegawai Tetap / Kontrak / Honorer" },
  { kind: "bidang-kerja",       label: "Bidang Kerja",       icon: Briefcase, placeholder: "Contoh: Teknik / Keuangan" },
  { kind: "jabatan-struktural", label: "Jabatan",            icon: Users2, placeholder: "Contoh: Manager Operasional" },
  { kind: "kode-pangkat",       label: "Kode Pangkat",       icon: Award,  placeholder: "Contoh: III-A" },
  { kind: "tingkat-jabatan",    label: "Tingkat Jabatan",    icon: Layers, placeholder: "Contoh: Tingkat 1" },
];

export default function AdminMasterKepegawaian() {
  const [tab, setTab] = useState(TABS[0].kind);

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="page-master-kepegawaian">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Konfigurasi Kebijakan
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Master Data Kepegawaian
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Kelola lima master data kepegawaian yang dipakai sebagai dropdown pada formulir data pegawai.
            Hanya Admin yang dapat menambah, mengubah, atau menghapus.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1" data-testid="master-kepegawaian-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.kind}
                value={t.kind}
                className="gap-2 data-[state=active]:bg-teal-700 data-[state=active]:text-white"
                data-testid={`master-kepegawaian-tab-${t.kind}`}
              >
                <Icon size={14} />
                {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.kind} value={t.kind} className="mt-6">
            <MasterKepegawaianSection kind={t.kind} label={t.label} placeholder={t.placeholder} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function MasterKepegawaianSection({ kind, label, placeholder }) {
  const endpoint = `/master/kepegawaian/${kind}`;
  const testPrefix = `mk-${kind}`;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nama: "", kode: "", deskripsi: "", is_active: true });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setRows(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.nama || "").toLowerCase().includes(q) ||
      (r.kode || "").toLowerCase().includes(q) ||
      (r.deskripsi || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  function openCreate() {
    setEditing(null);
    setForm({ nama: "", kode: "", deskripsi: "", is_active: true });
    setOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setForm({
      nama: row.nama || "",
      kode: row.kode || "",
      deskripsi: row.deskripsi || "",
      is_active: row.is_active !== false,
    });
    setOpen(true);
  }
  async function save() {
    if (!form.nama.trim()) {
      toast.error("Nama wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nama: form.nama.trim(),
        kode: form.kode.trim() || null,
        deskripsi: form.deskripsi.trim() || null,
        is_active: !!form.is_active,
      };
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success(`${label} berhasil diperbarui`);
      } else {
        await api.post(endpoint, payload);
        toast.success(`${label} berhasil ditambahkan`);
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }
  async function toggleAktif(row) {
    try {
      await api.put(`${endpoint}/${row.id}`, {
        nama: row.nama,
        kode: row.kode || null,
        deskripsi: row.deskripsi || null,
        is_active: !(row.is_active !== false),
      });
      toast.success(`${row.nama} ${row.is_active !== false ? "dinonaktifkan" : "diaktifkan"}`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }
  async function del(row) {
    if (!confirm(`Hapus ${label} "${row.nama}"?\n\nBila masih dipakai pegawai, akan dinonaktifkan (soft-delete).`)) return;
    try {
      const { data } = await api.delete(`${endpoint}/${row.id}`);
      if (data?.soft_deleted) toast.warning(data.detail || `${row.nama} dinonaktifkan`);
      else toast.success(`${row.nama} dihapus`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  return (
    <div data-testid={`${testPrefix}-section`}>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative sm:max-w-xs flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={`Cari ${label.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid={`${testPrefix}-search`}
          />
        </div>
        <Button
          onClick={openCreate}
          className="gap-2 bg-teal-700 hover:bg-teal-800"
          data-testid={`${testPrefix}-add-btn`}
        >
          <Plus size={16} /> Tambah {label}
        </Button>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 w-32">Kode</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nama</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Deskripsi</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 w-24">Status</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 w-32 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">Memuat…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-10">
                Belum ada {label.toLowerCase()}. Klik tombol di atas untuk menambahkan.
              </TableCell></TableRow>
            ) : filtered.map((r) => (
              <TableRow key={r.id} data-testid={`${testPrefix}-row-${r.id}`}>
                <TableCell><span className="font-mono-clock text-xs text-slate-600">{r.kode || "—"}</span></TableCell>
                <TableCell><span className="font-semibold text-slate-900">{r.nama}</span></TableCell>
                <TableCell><span className="text-slate-600 text-sm">{r.deskripsi || <span className="text-slate-300">—</span>}</span></TableCell>
                <TableCell>
                  {r.is_active !== false ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200">Aktif</Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-600">Nonaktif</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => toggleAktif(r)} data-testid={`${testPrefix}-toggle-${r.id}`}
                      title={r.is_active !== false ? "Nonaktifkan" : "Aktifkan"}>
                      <Power size={14} className={r.is_active !== false ? "text-emerald-600" : "text-slate-400"} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`${testPrefix}-edit-${r.id}`}>
                      <Pencil size={14} className="text-teal-700" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(r)} data-testid={`${testPrefix}-del-${r.id}`}>
                      <Trash2 size={14} className="text-rose-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${label}` : `Tambah ${label}`}</DialogTitle>
            <DialogDescription>
              Isi minimal Nama. Kode & deskripsi opsional. Nonaktifkan bila tidak dipakai lagi
              tanpa harus menghapus histori.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama</Label>
              <Input
                className="mt-1.5"
                placeholder={placeholder}
                value={form.nama}
                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                maxLength={120}
                data-testid={`${testPrefix}-input-nama`}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Kode (opsional)</Label>
              <Input
                className="mt-1.5"
                value={form.kode}
                onChange={(e) => setForm({ ...form, kode: e.target.value })}
                maxLength={40}
                data-testid={`${testPrefix}-input-kode`}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Deskripsi (opsional)</Label>
              <Textarea
                className="mt-1.5"
                value={form.deskripsi}
                onChange={(e) => setForm({ ...form, deskripsi: e.target.value })}
                maxLength={400}
                rows={3}
                data-testid={`${testPrefix}-input-deskripsi`}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">Aktif</div>
                <div className="text-xs text-slate-500">
                  Hanya {label.toLowerCase()} aktif yang tampil di dropdown profil pegawai.
                </div>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: !!v })}
                data-testid={`${testPrefix}-input-active`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testPrefix}-cancel-btn`}>Batal</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid={`${testPrefix}-save-btn`}>
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
